// Единый клиентский fetch с прозрачным refresh-on-401 для data-доменов
// (news/profile/categories). fix-auth-session-lifetime.
//
// Контекст проблемы (баг «навигация между защищёнными страницами после простоя →
// "Сессия истекла, войдите снова", а F5 лечит»):
//   • Все защищённые страницы — клиентские компоненты (`'use client'`), фетчат
//     данные через `useEffect` → `*-client.ts` → `/api/...`.
//   • При клиент-сайд навигации (Link/router.push) Next.js НЕ перезапускает общий
//     `(protected)/layout.tsx` (layout персистится) → серверный `SessionRefreshBridge`
//     из первого фикса не срабатывает.
//   • Access-токен истекает через 15 мин → data-route-handler возвращает 401 →
//     страница показывала «Сессия истекла…» вместо прозрачного refresh.
//
// Решение: эта обёртка перехватывает 401, прозрачно вызывает `POST /api/auth/refresh`
// (route handler легально ротирует cookie), дедуплит параллельные 401 через
// модульный Promise-замок, и при успехе — один раз ретраит исходный запрос.
// При неудачном refresh — редирект на /login (`window.location`: интерсептор живёт
// вне React-context, `useRouter` недоступен).
//
// Кого НЕ оборачивать: `auth-client.ts` (refresh→refresh = петля, login/refresh/
// logout сами управляют сессией). Здесь — только data-домены.

import { PortalApiError } from './errors';

interface ErrorResponseBody {
  success: false;
  error: {
    code: string;
    message: string;
    fields?: Record<string, string>;
  };
}

/**
 * Модульный замок refresh: параллельные 401 (напр. news-feed + categories-tree,
* летящие одновременно при заходе на страницу) ждут один refresh, а не плодят
 * запросы. Строгая серверная ротация пропустит только первый; лишние запросы
 * бессмысленны и нагружают бэкенд.
 */
let refreshPromise: Promise<boolean> | null = null;

/**
 * Канонический `RequestInit` для `POST /api/auth/refresh` (FE-05: ранее
 * дублировался в трёх местах — `session-refresh-bridge.tsx`, `refreshOnce`
 * здесь, `refreshSession` в `auth-client.ts`).
 *
 * Тело — пустой JSON-объект `'{}'`: refresh-cookie прикрепляется браузером
 * (httpOnly, ARCH-D-028); server route handler читает её через серверный
 * `portalFetch`. `credentials: 'same-origin'` обязателен для проброса cookie.
 *
 * Не возвращает URL — caller контролирует сам (parity `commonInit()` в
 * `auth-client.ts`, который здесь заменяем на `{...refreshRequestInit()}`).
 */
export function refreshRequestInit(): RequestInit {
  return {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: '{}',
  };
}

/**
 * Один прозрачный refresh сессии. Возвращает `true` при успехе (cookie
 * ротированы), `false` при неудаче (refresh невалиден/отозван — сессия умерла).
 * Дедуплит параллельные вызовы через `refreshPromise`.
 */
function refreshOnce(): Promise<boolean> {
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    try {
      const res = await fetch('/api/auth/refresh', refreshRequestInit());
      return res.ok;
    } catch {
      // Сетевой сбой — трактуем как неудачу (caller решает: бросить оригинал или
      // редирект). Не маскируем под успех, чтобы не зациклить retry.
      return false;
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
}

/**
 * Редирект на /login при финальной потере сессии. `window.location` (не
 * `router.replace`): интерсептор — plain-функция вне React-context. Гард
 * `typeof window` — на случай SSR-вызова (defensive; data-клиенты client-only).
 */
function bailToLogin(): void {
  if (typeof window !== 'undefined') {
    window.location.assign('/login');
  }
}

/**
 * Парсит BFF-ответ; на `success:false` бросает `PortalApiError` (единая логика
 * для всех data-клиентов — прежде дублировалась в каждом `*-client.ts`).
 */
export async function parseApiResponse<T extends { success: true }>(
  res: Response,
): Promise<T> {
  let body: unknown;
  try {
    body = await res.json();
  } catch {
    throw new PortalApiError('INTERNAL_ERROR', res.status || 500, 'Внутренняя ошибка');
  }

  if (
    body &&
    typeof body === 'object' &&
    (body as { success?: unknown }).success === true
  ) {
    return body as T;
  }

  const errBody = body as ErrorResponseBody | undefined;
  const code = errBody?.error?.code ?? 'INTERNAL_ERROR';
  const message = errBody?.error?.message ?? 'Внутренняя ошибка';
  throw new PortalApiError(
    code,
    res.status || 500,
    message,
    errBody?.error?.fields,
  );
}

/**
 * Клиентский fetch с прозрачным refresh-on-401. Обёртка над `fetch` для data-
 * доменов (news/profile/categories): на HTTP 401 один раз refresh + retry.
 *
 * Семантика retry: только один цикл (401 → refresh → retry), без рекурсии —
 * иначе зациклился бы при постоянно отзывающемся refresh. Параметры и `init`
 * переиспользуются для retry как есть (clone body не нужен — `init.body`
 * строковый/FormData, читается повторно).
 *
 * На финальную потереру сессии (refresh тоже невалиден) — редирект /login и
 * бросок оригинальной ошибки (чтобы caller не ушёл в success-ветку; на практике
 * редирект уже уводит страницу).
 */
export async function clientFetch(
  input: string,
  init: RequestInit,
): Promise<Response> {
  const res = await fetch(input, init);

  if (res.status !== 401) {
    return res;
  }

  // 401 — access истёк. Пробуем один refresh; при успехе ретраим исходный запрос.
  const refreshed = await refreshOnce();
  if (!refreshed) {
    // Refresh невалиден/отозван — сессия умерла. Уводим на /login.
    bailToLogin();
    return res; // (парсится caller'ом как PortalApiError UNAUTHENTICATED)
  }

  // Cookie ротированы — повторяем исходный запрос с уже валидным access.
  return fetch(input, init);
}
